import { initializeApp } from 'firebase/app';
import { getFirestore, doc, setDoc, serverTimestamp, getDoc } from 'firebase/firestore';
import { getAuth, setPersistence, browserLocalPersistence } from 'firebase/auth';
import { getFunctions, connectFunctionsEmulator, httpsCallable, setCustomAuthTokenHeader } from 'firebase/functions';
import { getStorage } from 'firebase/storage';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID
};

const app = initializeApp(firebaseConfig);
const db = getFirestore(app);
const auth = getAuth(app);
const functions = getFunctions(app, 'us-central1');
const storage = getStorage(app);
const messaging = typeof window !== 'undefined' && 'serviceWorker' in navigator ? getMessaging(app) : null;

// Sæt auth persistence til LOCAL for at forhindre udløb af sessioner
setPersistence(auth, browserLocalPersistence)
  .catch((error) => {
    console.error('Fejl ved indstilling af auth persistence:', error);
  });

// Hvis vi kører i udvikling, tilslut til lokale emulatorer
if (window.location.hostname === 'localhost') {
  try {
    connectFunctionsEmulator(functions, 'localhost', 5001);
    console.log('Tilsluttet Functions emulator på localhost:5001');
  } catch (error) {
    console.warn('Kunne ikke tilslutte til Functions emulator:', error);
  }
}

// Hjælpefunktion til at anmode om FCM-tilladelse og registrere enheden
const requestNotificationPermission = async (userId) => {
  if (!messaging) return { success: false, error: 'Messaging ikke understøttet i denne browser' };

  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      return { success: false, error: 'Notifikationstilladelse ikke givet' };
    }

    const token = await getToken(messaging, {
      vapidKey: "BAPLHmT3ApAgBQ4hNrNy8yljxS_MBPLG_Es-gILds5ii0Ec81S5xYD8kL3zgkXltYzzrQmRRo8hs92ZQbK70-hQ"
    });

    if (token) {
      // Gem token i Firestore til senere brug
      await setDoc(doc(db, 'users', userId, 'fcm_tokens', token), {
        token,
        device: navigator.userAgent,
        createdAt: serverTimestamp(),
        lastActive: serverTimestamp()
      });
      return { success: true, token };
    } else {
      return { success: false, error: 'Kunne ikke få FCM-token' };
    }
  } catch (error) {
    console.error('Fejl ved anmodning om notifikationstilladelse:', error);
    return { success: false, error: error.message };
  }
};

// Lyt efter beskeder i forgrunden
const onMessageListener = () => {
  return new Promise((resolve) => {
    if (!messaging) {
      resolve(null);
      return;
    }
    
    onMessage(messaging, (payload) => {
      resolve(payload);
    });
  });
};

// Hjælpefunktion til at få en bruger-ID - returnerer enten den valgte kunde eller den aktuelle bruger
const getEffectiveUserId = (selectedCustomer) => {
  if (selectedCustomer && selectedCustomer.id) {
    return selectedCustomer.id;
  }
  
  if (auth.currentUser) {
    return auth.currentUser.uid;
  }
  
  return null;
};

// Hjælpefunktion til at få brugerdata, enten fra den valgte kunde eller den aktuelle bruger
const getEffectiveUserData = async (selectedCustomer) => {
  const userId = getEffectiveUserId(selectedCustomer);
  
  if (!userId) {
    return null;
  }
  
  // Hvis det er en valgt kunde, returner kundens data
  if (selectedCustomer) {
    return {
      id: selectedCustomer.id,
      ...selectedCustomer
    };
  }
  
  // Hvis det er den aktuelle bruger, hent data fra Firebase
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists()) {
      return {
        id: userId,
        ...userDoc.data()
      };
    }
  } catch (error) {
    console.error('Fejl ved hentning af brugerdata:', error);
  }
  
  return null;
};

export { 
  db, 
  auth, 
  functions, 
  storage, 
  messaging, 
  requestNotificationPermission, 
  onMessageListener,
  getEffectiveUserId,
  getEffectiveUserData
};